import { CheckCircleOutlined, UserOutlined } from '@ant-design/icons';
import { translate, TranslationEntity } from '@repo/utils';
import {
  Col,
  Divider, Drawer, Flex, Row, Typography,
} from 'antd';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import {
  automationDataSelector,
  automationModeSelector,
  isReportPanelOpenSelector,
} from 'automations/selectors/automationConfig';
import { totalLeadsCompletedCountSelector, totalLeadsEnteredCountSelector } from 'automations/selectors/reactFlow';
import { setReportPanelOpen } from 'automations/store/automationConfigSlice';
import { AutomationMode } from 'automations/types/automation';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import styles from './AutomationCommonReportPanel.module.scss';

export default function AutomationCommonReportPanel() {
  const mode = useSelector(automationModeSelector);
  const reportPanelOpen = useSelector(isReportPanelOpenSelector);
  const dispatch = useAutomationDispatch();

  const totalEnteredLeadCount = useSelector(totalLeadsEnteredCountSelector);
  const totalCompletedLeadCount = useSelector(totalLeadsCompletedCountSelector);
  const automationData = useSelector(automationDataSelector);

  const handleClose = useCallback(() => {
    dispatch(setReportPanelOpen(false));
  }, [dispatch]);

  const createdBy = automationData?.createdBy?.fullName;
  const createdAt = dayjs(automationData?.createdAt);
  const updatedBy = automationData?.updatedBy?.fullName;
  const updatedAt = dayjs(automationData?.updatedAt);

  if (mode === AutomationMode.commonReport) {
    return (
      <Drawer
        onClose={handleClose}
        className={styles.drawer}
        classNames={{
          wrapper: styles.drawer,
          body: styles.body,
        }}
        title={translate(`#{${TranslationEntity.automation}} Report`, [{
          capitalize: true,
        }])}
        destroyOnClose
        open={reportPanelOpen}
        getContainer={false}
        mask={false}
      >
        {Number.isInteger(totalEnteredLeadCount) && (
          <Flex vertical className={styles.contentCard}>
            <Flex align="center" gap={4}>
              <UserOutlined className={styles.icon} />
              <Typography.Text type="secondary">
                {translate(`Total #{${TranslationEntity.lead}} Entered`, [{
                  capitalize: true,
                  pluralize: true,
                }])}
              </Typography.Text>
            </Flex>
            <Typography.Title level={4} className={styles.title}>{totalEnteredLeadCount}</Typography.Title>
          </Flex>
        )}
        {Number.isInteger(totalCompletedLeadCount) && (
          <Flex vertical className={styles.contentCard}>
            <Flex align="center" gap={4}>
              <CheckCircleOutlined className={styles.icon} />
              <Typography.Text type="secondary">
                {translate(`Total #{${TranslationEntity.lead}} Completed`, [{
                  capitalize: true,
                  pluralize: true,
                }])}
              </Typography.Text>
            </Flex>
            <Typography.Title level={4} className={styles.title}>{totalCompletedLeadCount}</Typography.Title>
          </Flex>
        )}

        <div className={styles.footerContent}>
          <Divider className={styles.divider} />
          <Flex vertical gap={8}>
            {createdBy && (
              <Row>
                <Col span={12}>
                  <Typography.Text type="secondary">Created By</Typography.Text>
                </Col>
                <Col span={12}>
                  <Typography.Text type="secondary">{createdBy}</Typography.Text>
                </Col>
              </Row>
            )}
            {createdAt.isValid() && (
              <Row>
                <Col span={12}>
                  <Typography.Text type="secondary">Created At</Typography.Text>
                </Col>
                <Col span={12}>
                  <Typography.Text type="secondary">
                    {createdAt.format('MMM D, YYYY h:mm A')}
                  </Typography.Text>
                </Col>
              </Row>
            )}
            {updatedBy && (
              <Row>
                <Col span={12}>
                  <Typography.Text type="secondary">Updated By</Typography.Text>
                </Col>
                <Col span={12}>
                  <Typography.Text type="secondary">{updatedBy}</Typography.Text>
                </Col>
              </Row>
            )}
            {updatedAt.isValid() && (
              <Row>
                <Col span={12}>
                  <Typography.Text type="secondary">Updated At</Typography.Text>
                </Col>
                <Col span={12}>
                  <Typography.Text type="secondary">
                    {updatedAt.format('MMM D, YYYY h:mm A')}
                  </Typography.Text>
                </Col>
              </Row>
            )}
          </Flex>
        </div>
      </Drawer>
    );
  } else {
    return null;
  }
}
